import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  AlertTriangle, CalendarClock, ClipboardList, Lock, Pencil, Users, Gavel,
} from 'lucide-react';

import api from '../../../services/api';
import { readItem } from '../../../utils/apiResult';
import { useAuth } from '../../../context/AuthContext';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import Button from '../../../components/ui/Button';
import IconButton from '../../../components/ui/IconButton';
import Tooltip from '../../../components/ui/Tooltip';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import ScorecardFillDialog from '../ScorecardFillDialog';
import { employeeDisplayName } from '../../ats-requisitions/employeeOption';
import DebriefComparisonTable, { Pill, REC_TONE } from './DebriefComparisonTable';
import {
  useInterviewDebrief, saveDebrief, fmtScore, RECOMMENDATION_ORDER, DECISION_TONE,
} from './debriefModel';
import {
  buildDebriefSchema, debriefInitialValues, toDebriefRequest, blockedByPending,
} from './debriefSchema';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 32px;
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  padding: 18px 22px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.dark.little};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 12px;
`;

const HeadLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin-top: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
`;

const Stat = styled.div`
  background: #FAFBFC;
  border: 1px solid #EEF1F4;
  border-radius: 8px;
  padding: 10px 14px;
`;

const StatLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const StatValue = styled.div`
  font-size: 18px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 2px;
`;

const RecBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Banner = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 18px;
  border-radius: 10px;
  background: ${({ $tone }) => ($tone === 'warning' ? '#FFF5E1' : '#EEF6FD')};
  border: 1px solid ${({ $tone }) => ($tone === 'warning' ? '#F0C36D' : '#BFD9F2')};
  color: ${({ $tone }) => ($tone === 'warning' ? '#7A5200' : '#024F87')};
  font-size: 13px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 48px 24px;
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const EmptyTitle = styled.div`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Field = styled.div`
  padding: 8px 0;
  border-top: 1px solid #F2F4F7;
  font-size: 14px;
  &:first-of-type { border-top: 0; }
`;

const FieldLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 2px;
`;

const Prose = styled.div`
  white-space: pre-wrap;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const NoteList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  li { padding: 8px 0; border-top: 1px solid #F2F4F7; font-size: 13px; }
  li:first-child { border-top: 0; }
`;

const fmtDateTime = (d, lang) => {
  if (!d) return null;
  try { return new Date(d).toLocaleString(lang || 'tr'); } catch { return null; }
};

export default function AtsInterviewDebriefPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { id } = useParams();
  const history = useHistory();
  const { canWrite } = useAuth();
  const canWriteAts = canWrite ? canWrite('AT') : false;

  const { status, data, reload } = useInterviewDebrief(id);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scorecard, setScorecard] = useState({ open: false, interview: null });

  const evaluators = useMemo(() => (Array.isArray(data?.evaluators) ? data.evaluators : []), [data]);
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const interview = data?.interview || null;
  const debrief = data?.debrief || null;
  const allSubmitted = data?.allSubmitted === true;
  const canRecord = data?.canRecord === true;
  // Bekleyen skorkartı gerekçeyle geçebilme yetkisi SUNUCUDAN okunur
  // (`canOverride` = AT.Write/admin). Alan gelmezse istemci yetkisine düşülür.
  const writable = typeof data?.canOverride === 'boolean' ? data.canOverride : canWriteAts;

  // Kişi seçici: önce paneldeki değerlendiriciler (ad sunucuda çözülmüş),
  // sonra çalışan araması — mülakat planlama seçicisiyle aynı sözleşme.
  const knownPeople = useMemo(() => {
    const map = {};
    evaluators.forEach((e) => { if (e.userId) map[e.userId] = { id: e.userId, name: e.name }; });
    (debrief?.attendees || []).forEach((a) => { if (a.userId && a.name) map[a.userId] = { id: a.userId, name: a.name }; });
    if (debrief?.facilitatorUserId && debrief?.facilitatorName) {
      map[debrief.facilitatorUserId] = { id: debrief.facilitatorUserId, name: debrief.facilitatorName };
    }
    return map;
  }, [evaluators, debrief]);

  const searchPeople = useCallback(async (q) => {
    const needle = (q || '').toLocaleLowerCase('tr-TR');
    const panel = Object.values(knownPeople)
      .filter((p) => !needle || (p.name || '').toLocaleLowerCase('tr-TR').includes(needle));
    const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const r = res?.data?.data;
    const items = Array.isArray(r) ? r : (r?.data || []);
    const seen = new Set(panel.map((p) => p.id));
    const emps = items
      .filter((e) => !seen.has(e.id))
      .map((e) => ({ id: e.id, name: employeeDisplayName(e), profilePicture: e.profilePicture }));
    return [...panel, ...emps];
  }, [knownPeople]);

  const getPersonById = useCallback(async (pid) => {
    if (knownPeople[pid]) return knownPeople[pid];
    const e = readItem(await api.get(`/v1/employees/${pid}`));
    // Ham kimlik ASLA gösterilmez — çözülemeyen kişi nötr etiketle çizilir.
    return e
      ? { id: pid, name: employeeDisplayName(e), profilePicture: e.profilePicture }
      : { id: pid, name: t('ats.debrief.unknownPerson') };
  }, [knownPeople, t]);

  const schema = useMemo(() => buildDebriefSchema({
    t, allSubmitted, writable, searchPeople, getPersonById,
  }), [t, allSubmitted, writable, searchPeople, getPersonById]);

  const initialValues = useMemo(
    () => (drawerOpen ? debriefInitialValues(debrief, { evaluators }) : null),
    // Çekmece her açılışta tazelenir; açıkken değerler sabit kalmalı.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drawerOpen],
  );

  const handleSave = useCallback(async (form) => {
    const body = toDebriefRequest(form, { allSubmitted, writable });
    const ok = await saveDebrief(id, body, { isEdit: !!debrief });
    if (ok) reload();
    return ok;
  }, [id, allSubmitted, writable, debrief, reload]);

  const openMyScorecard = useCallback(async () => {
    const iv = readItem(await api.get(`/v1/ats-interviews/${id}`));
    if (iv) setScorecard({ open: true, interview: iv });
  }, [id]);

  const crumbs = [
    { label: t('ats.interview.pageTitle'), path: '/ats/interviews' },
    { label: interview?.candidateFullName
      ? `${interview.candidateFullName} — ${t('ats.debrief.title')}`
      : t('ats.debrief.title') },
  ];

  const scorecardDialog = (
    <ScorecardFillDialog
      open={scorecard.open}
      interview={scorecard.interview}
      onClose={() => setScorecard({ open: false, interview: null })}
      onSaved={() => { setScorecard({ open: false, interview: null }); reload(); }}
    />
  );

  if (status === 'loading') {
    return (
      <Page>
        <Breadcrumbs items={crumbs} />
        <EmptyState>{t('common.loading')}</EmptyState>
      </Page>
    );
  }

  if (status === 'submitOwnFirst') {
    return (
      <Page>
        <Breadcrumbs items={crumbs} />
        <EmptyState data-role="debrief-submit-own-first">
          <Lock size={28} />
          <EmptyTitle>{t('ats.debrief.submitOwnFirst.title')}</EmptyTitle>
          <div>{t('ats.debrief.submitOwnFirst.body')}</div>
          <Button onClick={openMyScorecard} data-role="debrief-fill-my-scorecard">
            <ClipboardList size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            {t('ats.debrief.submitOwnFirst.action')}
          </Button>
        </EmptyState>
        {scorecardDialog}
      </Page>
    );
  }

  if (status === 'failed' || !data) {
    return (
      <Page>
        <Breadcrumbs items={crumbs} />
        <EmptyState data-role="debrief-load-error" role="alert">
          <AlertTriangle size={28} />
          <EmptyTitle>{t('ats.debrief.notFound.title')}</EmptyTitle>
          <div>{t('ats.debrief.notFound.body')}</div>
          <Button variant="outline" onClick={() => history.push('/ats/interviews')}>
            {t('ats.debrief.backToInterviews')}
          </Button>
        </EmptyState>
      </Page>
    );
  }

  const pendingCount = evaluators.filter((e) => e.isRequired !== false && !e.submitted).length;
  const counts = data.recommendationCounts || {};
  const submittedNotes = evaluators.filter((e) => e.submitted && e.freeTextNotes);
  const blocked = blockedByPending({ allSubmitted, writable });

  return (
    <Page>
      <Breadcrumbs items={crumbs} />

      <Card>
        <Title>{interview?.candidateFullName || '—'}</Title>
        <Meta>
          {interview?.jobPostingTitle && <span>{interview.jobPostingTitle}</span>}
          {interview?.interviewType && (
            <span>{t(`ats.interview.type.${interview.interviewType}`, { defaultValue: interview.interviewType })}</span>
          )}
          {interview?.scheduledStart && (
            <span><CalendarClock size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              {fmtDateTime(interview.scheduledStart, lang)}
            </span>
          )}
          {data.template?.name && <span>{data.template.name}</span>}
          {data.debriefRequired && <Pill $tone="blue">{t('ats.debrief.requiredBadge')}</Pill>}
          {interview?.applicationId && (
            <Button
              variant="outline"
              onClick={() => history.push(`/ats/applications/${interview.applicationId}`)}
            >
              {t('ats.debrief.openApplication')}
            </Button>
          )}
        </Meta>
      </Card>

      {!allSubmitted && (
        <Banner $tone="warning" data-role="debrief-pending-banner">
          <AlertTriangle size={18} />
          <div>{t('ats.debrief.pendingScorecards', { count: pendingCount })}</div>
        </Banner>
      )}

      <Card>
        <CardHeader><HeadLeft><Users size={14} />{t('ats.debrief.section.summary')}</HeadLeft></CardHeader>
        <Stats>
          <Stat>
            <StatLabel>{t('ats.debrief.overall.avg')}</StatLabel>
            <StatValue data-role="debrief-overall-avg">{fmtScore(data.overall?.avg, lang) ?? '—'}</StatValue>
          </Stat>
          <Stat>
            <StatLabel>{t('ats.debrief.overall.min')}</StatLabel>
            <StatValue>{fmtScore(data.overall?.min, lang) ?? '—'}</StatValue>
          </Stat>
          <Stat>
            <StatLabel>{t('ats.debrief.overall.max')}</StatLabel>
            <StatValue>{fmtScore(data.overall?.max, lang) ?? '—'}</StatValue>
          </Stat>
        </Stats>
        <div style={{ marginTop: 14 }}>
          <StatLabel style={{ marginBottom: 6 }}>{t('ats.debrief.recommendationDistribution')}</StatLabel>
          <RecBar data-role="debrief-recommendations">
            {RECOMMENDATION_ORDER.map((k) => (
              <Pill key={k} $tone={(counts[k] || 0) > 0 ? REC_TONE[k] : 'neutral'}>
                {t(`ats.scorecard.recommendation.${k}`)}: {counts[k] || 0}
              </Pill>
            ))}
          </RecBar>
        </div>
      </Card>

      {rows.length === 0 ? (
        <EmptyState>{t('ats.debrief.noScores')}</EmptyState>
      ) : (
        <DebriefComparisonTable evaluators={evaluators} rows={rows} />
      )}

      {submittedNotes.length > 0 && (
        <Card>
          <CardHeader><HeadLeft>{t('ats.debrief.section.notes')}</HeadLeft></CardHeader>
          <NoteList>
            {submittedNotes.map((e) => (
              <li key={e.userId}>
                <strong>{e.name || t('ats.debrief.unknownEvaluator')}</strong>
                <Prose>{e.freeTextNotes}</Prose>
              </li>
            ))}
          </NoteList>
        </Card>
      )}

      <Card data-role="debrief-decision-card">
        <CardHeader>
          <HeadLeft><Gavel size={14} />{t('ats.debrief.section.decision')}</HeadLeft>
          {debrief && canRecord && !blocked && (
            <Tooltip text={t('ats.debrief.edit')}>
              <IconButton size="sm" aria-label={t('ats.debrief.edit')} onClick={() => setDrawerOpen(true)} data-role="debrief-edit">
                <Pencil size={14} />
              </IconButton>
            </Tooltip>
          )}
        </CardHeader>

        {debrief ? (
          <div data-role="debrief-record">
            <Field>
              <FieldLabel>{t('ats.debrief.form.decision')}</FieldLabel>
              <Pill $tone={DECISION_TONE[debrief.decision]}>
                {t(`ats.debrief.decision.${debrief.decision}`, { defaultValue: debrief.decision })}
              </Pill>
            </Field>
            <Field>
              <FieldLabel>{t('ats.debrief.form.heldAt')}</FieldLabel>
              {fmtDateTime(debrief.heldAt, lang) || '—'}
            </Field>
            {debrief.facilitatorName && (
              <Field>
                <FieldLabel>{t('ats.debrief.form.facilitator')}</FieldLabel>
                {debrief.facilitatorName}
              </Field>
            )}
            {Array.isArray(debrief.attendees) && debrief.attendees.length > 0 && (
              <Field>
                <FieldLabel>{t('ats.debrief.form.attendees')}</FieldLabel>
                {debrief.attendees.map((a) => a.name).filter(Boolean).join(', ')}
              </Field>
            )}
            <Field>
              <FieldLabel>{t('ats.debrief.form.summary')}</FieldLabel>
              <Prose>{debrief.summary}</Prose>
            </Field>
            {debrief.overrideReason && (
              <Field>
                <FieldLabel>{t('ats.debrief.form.overrideReason')}</FieldLabel>
                <Prose>{debrief.overrideReason}</Prose>
              </Field>
            )}
            {debrief.recordedByName && (
              <Meta>{t('ats.debrief.recordedBy', { name: debrief.recordedByName })}</Meta>
            )}
          </div>
        ) : !canRecord ? (
          <Meta data-role="debrief-cannot-record">{t('ats.debrief.cannotRecord')}</Meta>
        ) : blocked ? (
          <Meta data-role="debrief-blocked">{t('ats.debrief.blockedByPending')}</Meta>
        ) : (
          <div>
            <Meta style={{ marginBottom: 12 }}>{t('ats.debrief.noDecisionYet')}</Meta>
            <Button onClick={() => setDrawerOpen(true)} data-role="debrief-record-open">
              {t('ats.debrief.record')}
            </Button>
          </div>
        )}
      </Card>

      {canRecord && (
        <GenericFormDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title={debrief ? t('ats.debrief.edit') : t('ats.debrief.record')}
          schema={schema}
          initialValues={initialValues}
          onSave={handleSave}
        />
      )}
      {scorecardDialog}
    </Page>
  );
}
